import {
  ListingModalOptionHeader,
  ListingModalOptionTitle,
  ListingModalOptionWrapper,
  useListingModalOptionContext,
  useListingModalPlanOptionsContext,
} from "../listingModalPlanOptions";
import { capitalizeFirstLetter } from "../../../../../lib/global/capitalizeFirstLetter";
import { AppPrimaryButton } from "../../../../global/ui/buttons";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../../../redux/store";
import { useSnackbarContext } from "../../../../global/snackbar/SnackbarProvider";
import useUser from "../../../../../hooks/user/useUser";
import { useListingModalSwapOneTimeContext } from "./ListingModalSwapOneTimeProvider";
import { useModalContext } from "../../../../global/modals/ModalProvider";
import { ListingModalSwapLimitReached } from "../listingModalSwapLimitReached/ListingModalSwapLimitReached";
import {
  setCurrentSwaps,
  setOwnedListings,
} from "../../../../../redux/pages/trainingPlanSlice";
import { ErrorSnackbar } from "../../../../global/snackbar";

export const ListingSwapOption = () => {
  const listingOptionContext = useListingModalOptionContext();
  const { state } = useListingModalSwapOneTimeContext();
  const { handleGetOwnedListings, handleExecutePurchase } =
    useListingModalPlanOptionsContext();
  const swaps = useSelector((state: RootState) => state.trainingPlan.swaps);
  const { setSnackbar } = useSnackbarContext();
  const dispatch = useDispatch();
  const { user } = useUser();
  const currentListing = useSelector(
    (state: RootState) => state.trainingPlan.listingData,
  );
  const borrowedListings = useSelector(
    (state: RootState) => state.trainingPlan.borrowedListings,
  );
  const { showModal } = useModalContext();
  const shouldShowLimitReachedModal =
    state.subscription?.currentPlans <= borrowedListings.length;
  const userHasSwapsAvailable =
    swaps && user.subscription_data && swaps.available > 0;
  const ownedListings = useSelector(
    (state: RootState) => state.trainingPlan.ownedListings,
  );
  const currentListingIsOwned = !!ownedListings.find(
    (item) => item.id === currentListing.id,
  );

  const handleSwapPurchase = async () => {
    try {
      listingOptionContext.dispatch({ isLoading: true });
      const ownedListings = await handleGetOwnedListings();

      if (ownedListings.rows.find((item) => item.id === currentListing.id)) {
        throw new Error("You already own this training plan");
      }

      const executePurchaseRes = await handleExecutePurchase();

      dispatch(setOwnedListings([...ownedListings.rows, currentListing]));
      dispatch(setCurrentSwaps(executePurchaseRes));
    } catch (e) {
      const error = e as Error;
      setSnackbar(<ErrorSnackbar message={error.message} />);
    } finally {
      listingOptionContext.dispatch({ isLoading: false });
    }
  };

  const handleLimitReachedModal = () => {
    showModal(<ListingModalSwapLimitReached />);
  };

  if (currentListing.price === 0) {
    return null;
  }

  return (
    <ListingModalOptionWrapper>
      <ListingModalOptionHeader>
        <ListingModalOptionTitle>
          {userHasSwapsAvailable
            ? `Activate this training plan with your ${capitalizeFirstLetter(
                user.subscription_data.subscriptionPlan.type,
              )} membership. You have ${
                swaps.available
              } more plan unlocks this month`
            : `You have used all ${state.subscription?.totalPlans} of your training plan unlocks this month`}
        </ListingModalOptionTitle>
      </ListingModalOptionHeader>

      <AppPrimaryButton
        disabled={listingOptionContext.state.isLoading || currentListingIsOwned}
        onClick={
          shouldShowLimitReachedModal
            ? handleLimitReachedModal
            : handleSwapPurchase
        }
        className={"w-full text-[14px] md:text-[12px] xl:text-[14px]"}
      >
        Unlock {swaps?.available} of {state.subscription?.totalPlans} remaining
      </AppPrimaryButton>
    </ListingModalOptionWrapper>
  );
};
